import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';

interface AnalyticsLineData {
  line_id: number | null;
  line_name: string;
  total_szt: number;
  total_kg: number;
  szt_per_hour: number;
  kg_per_hour: number;
}

interface AnalyticsMaterialData {
  line_id: number | null;
  line_name: string;
  material: string;
  total_szt: number;
  total_kg: number;
  szt_per_hour: number;
  kg_per_hour: number;
}

interface AnalyticsDowntimeData {
  line_id: number;
  line_name: string;
  type: string;
  count: number;
  total_seconds: number;
  total_minutes: number;
}

interface AnalyticsDowntimeDetail {
  id: number;
  line_id: number;
  line_name: string;
  type: string;
  started_at: string;
  ended_at: string;
  duration_seconds: number;
  user_name: string | null;
}

interface AnalyticsData {
  period: string;
  period_hours: number;
  since: string;
  line_id: number | null;
  global: {
    total_szt: number;
    total_kg: number;
    szt_per_hour: number;
    kg_per_hour: number;
  };
  per_line: AnalyticsLineData[];
  per_material: AnalyticsMaterialData[];
  downtime: AnalyticsDowntimeData[];
  downtime_details: AnalyticsDowntimeDetail[];
}

type PeriodOption = '1h' | '6h' | '1d' | '7d' | '14d' | '30d' | 'all';

interface ProductionLine {
  id: number;
  name: string;
}

const PERIOD_OPTIONS: { value: PeriodOption; label: string }[] = [
  { value: '1h', label: '1h' },
  { value: '6h', label: '6h' },
  { value: '1d', label: '24h' },
  { value: '7d', label: '7 dni' },
  { value: '14d', label: '14 dni' },
  { value: '30d', label: '30 dni' },
  { value: 'all', label: 'Od początku' },
];

const AnalyticsDashboard: React.FC = () => {
  const { t } = useLanguage();
  const [period, setPeriod] = useState<PeriodOption>('1d');
  const [lineId, setLineId] = useState<number | null>(null);
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [lines, setLines] = useState<ProductionLine[]>([]);
  const [expandedDowntime, setExpandedDowntime] = useState<string | null>(null);

  useEffect(() => {
    api.get('/production-lines')
      .then((res) => setLines(res.data))
      .catch((err) => console.error('Error fetching lines:', err));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ period });
    if (lineId) params.append('line_id', String(lineId));
    api.get(`/admin/analytics?${params}`)
      .then((res) => setData(res.data))
      .catch((err) => console.error('Error fetching analytics:', err))
      .finally(() => setLoading(false));
  }, [period, lineId]);

  const thStyle: React.CSSProperties = {
    padding: '10px 14px',
    textAlign: 'left',
    border: '1px solid #dee2e6',
    fontSize: '13px',
    fontWeight: 'bold',
    whiteSpace: 'nowrap',
  };

  const tdStyle: React.CSSProperties = {
    padding: '10px 14px',
    border: '1px solid #dee2e6',
    fontSize: '13px',
    textAlign: 'right',
  };

  return (
    <div className="card" style={{ marginTop: '20px' }}>
      <h2 style={{ margin: '0 0 20px 0', color: '#2d3748' }}>
        {t('analytics.title')}
      </h2>

      {/* Controls row */}
      <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '20px' }}>
        {/* Period selector */}
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {PERIOD_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setPeriod(opt.value)}
              style={{
                padding: '8px 16px',
                fontSize: '13px',
                fontWeight: '600',
                borderRadius: '6px',
                cursor: 'pointer',
                border: period === opt.value ? 'none' : '2px solid #667eea',
                background: period === opt.value
                  ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                  : 'white',
                color: period === opt.value ? 'white' : '#667eea',
                transition: 'all 0.2s',
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Line filter */}
        <select
          value={lineId ?? ''}
          onChange={(e) => setLineId(e.target.value ? Number(e.target.value) : null)}
          className="input"
          style={{ width: '200px' }}
        >
          <option value="">{t('analytics.allLines')}</option>
          {lines.map((line) => (
            <option key={line.id} value={line.id}>{line.name}</option>
          ))}
        </select>
      </div>

      {/* Loading */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '30px', color: '#6b7280' }}>
          <div className="spinner" style={{ margin: '0 auto 12px auto' }} />
          {t('analytics.loading')}
        </div>
      )}

      {/* Data */}
      {!loading && data && (
        <>
          {/* Global stat cards */}
          <div className="stats-grid" style={{ marginBottom: '24px' }}>
            <div className="stat-card">
              <h3>{t('analytics.producedSzt')}</h3>
              <p style={{ fontSize: '28px', fontWeight: '700', margin: '8px 0 0 0' }}>
                {data.global.total_szt.toLocaleString('pl-PL')}
              </p>
            </div>
            <div className="stat-card" style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)' }}>
              <h3>{t('analytics.producedKg')}</h3>
              <p style={{ fontSize: '28px', fontWeight: '700', margin: '8px 0 0 0' }}>
                {data.global.total_kg.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
            <div className="stat-card" style={{ background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)' }}>
              <h3>{t('analytics.efficiencySztH')}</h3>
              <p style={{ fontSize: '28px', fontWeight: '700', margin: '8px 0 0 0' }}>
                {data.global.szt_per_hour.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
            <div className="stat-card" style={{ background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' }}>
              <h3>{t('analytics.efficiencyKgH')}</h3>
              <p style={{ fontSize: '28px', fontWeight: '700', margin: '8px 0 0 0' }}>
                {data.global.kg_per_hour.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
          </div>

          {/* Per-line breakdown */}
          {data.per_line.length > 0 ? (
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ margin: '0 0 12px 0', color: '#4a5568' }}>{t('analytics.perLine')}</h3>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#667eea', color: 'white' }}>
                      <th style={{ ...thStyle, textAlign: 'left', color: 'white' }}>{t('analytics.line')}</th>
                      <th style={{ ...thStyle, textAlign: 'right', color: 'white' }}>SZT</th>
                      <th style={{ ...thStyle, textAlign: 'right', color: 'white' }}>KG</th>
                      <th style={{ ...thStyle, textAlign: 'right', color: 'white' }}>SZT/h</th>
                      <th style={{ ...thStyle, textAlign: 'right', color: 'white' }}>KG/h</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.per_line.map((row, i) => (
                      <tr key={row.line_id ?? `null-${i}`} style={{ backgroundColor: i % 2 === 0 ? 'white' : '#f9fafb', borderBottom: '1px solid #dee2e6' }}>
                        <td style={{ ...tdStyle, textAlign: 'left', fontWeight: '600' }}>{row.line_name}</td>
                        <td style={tdStyle}>{row.total_szt.toLocaleString('pl-PL')}</td>
                        <td style={tdStyle}>{row.total_kg.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        <td style={tdStyle}>{row.szt_per_hour.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        <td style={tdStyle}>{row.kg_per_hour.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <p style={{ color: '#6b7280', textAlign: 'center', padding: '20px' }}>
              {t('analytics.noData')}
            </p>
          )}

          {/* Per-material breakdown grouped by line */}
          {data.per_material.length > 0 && (() => {
            // Group materials by line
            const grouped: Record<string, { line_name: string; materials: typeof data.per_material }> = {};
            data.per_material.forEach((row) => {
              const key = row.line_name;
              if (!grouped[key]) {
                grouped[key] = { line_name: key, materials: [] };
              }
              grouped[key].materials.push(row);
            });
            const lineGroups = Object.values(grouped);

            return (
              <div>
                <h3 style={{ margin: '0 0 12px 0', color: '#4a5568' }}>{t('analytics.perMaterial')}</h3>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#667eea', color: 'white' }}>
                        <th style={{ ...thStyle, textAlign: 'left', color: 'white' }}>{t('analytics.material')}</th>
                        <th style={{ ...thStyle, textAlign: 'right', color: 'white' }}>SZT</th>
                        <th style={{ ...thStyle, textAlign: 'right', color: 'white' }}>KG</th>
                        <th style={{ ...thStyle, textAlign: 'right', color: 'white' }}>SZT/h</th>
                        <th style={{ ...thStyle, textAlign: 'right', color: 'white' }}>KG/h</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lineGroups.map((group) => (
                        <React.Fragment key={group.line_name}>
                          {/* Line header row */}
                          <tr style={{ backgroundColor: '#eef2ff', borderTop: '2px solid #667eea' }}>
                            <td colSpan={5} style={{ padding: '10px 14px', fontWeight: '700', fontSize: '14px', color: '#4338ca', border: '1px solid #dee2e6' }}>
                              {group.line_name}
                            </td>
                          </tr>
                          {/* Material rows */}
                          {group.materials.map((row, i) => (
                            <tr key={`${group.line_name}-${row.material}-${i}`} style={{ backgroundColor: i % 2 === 0 ? 'white' : '#f9fafb', borderBottom: '1px solid #dee2e6' }}>
                              <td style={{ ...tdStyle, textAlign: 'left', fontWeight: '600', paddingLeft: '28px' }}>{row.material}</td>
                              <td style={tdStyle}>{row.total_szt.toLocaleString('pl-PL')}</td>
                              <td style={tdStyle}>{row.total_kg.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                              <td style={tdStyle}>{row.szt_per_hour.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                              <td style={tdStyle}>{row.kg_per_hour.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            </tr>
                          ))}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}

          {/* Downtime breakdown */}
          {data.downtime && data.downtime.length > 0 && (() => {
            const grouped: Record<string, { line_name: string; entries: typeof data.downtime }> = {};
            data.downtime.forEach((row) => {
              const key = row.line_name;
              if (!grouped[key]) {
                grouped[key] = { line_name: key, entries: [] };
              }
              grouped[key].entries.push(row);
            });
            const lineGroups = Object.values(grouped);

            const formatDate = (dateStr: string) => {
              const d = new Date(dateStr);
              return d.toLocaleString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
            };

            const formatDuration = (seconds: number) => {
              const mins = Math.floor(seconds / 60);
              const secs = seconds % 60;
              return `${mins}m ${secs.toString().padStart(2, '0')}s`;
            };

            return (
              <div style={{ marginTop: '24px' }}>
                <h3 style={{ margin: '0 0 12px 0', color: '#4a5568' }}>Przestoje</h3>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#667eea', color: 'white' }}>
                        <th style={{ ...thStyle, textAlign: 'left', color: 'white' }}>Typ</th>
                        <th style={{ ...thStyle, textAlign: 'right', color: 'white' }}>Liczba</th>
                        <th style={{ ...thStyle, textAlign: 'right', color: 'white' }}>Laczny czas (min)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lineGroups.map((group) => (
                        <React.Fragment key={group.line_name}>
                          <tr style={{ backgroundColor: '#eef2ff', borderTop: '2px solid #667eea' }}>
                            <td colSpan={3} style={{ padding: '10px 14px', fontWeight: '700', fontSize: '14px', color: '#4338ca', border: '1px solid #dee2e6' }}>
                              {group.line_name}
                            </td>
                          </tr>
                          {group.entries.map((row, i) => {
                            const expandKey = `${group.line_name}-${row.type}`;
                            const isExpanded = expandedDowntime === expandKey;
                            const details = data.downtime_details?.filter(
                              (d) => d.line_name === group.line_name && d.type === row.type
                            ) || [];

                            return (
                              <React.Fragment key={expandKey}>
                                <tr
                                  style={{
                                    backgroundColor: i % 2 === 0 ? 'white' : '#f9fafb',
                                    borderBottom: '1px solid #dee2e6',
                                    cursor: details.length > 0 ? 'pointer' : 'default',
                                  }}
                                  onClick={() => {
                                    if (details.length > 0) {
                                      setExpandedDowntime(isExpanded ? null : expandKey);
                                    }
                                  }}
                                >
                                  <td style={{ ...tdStyle, textAlign: 'left', fontWeight: '600', paddingLeft: '28px' }}>
                                    <span style={{ marginRight: '8px', display: 'inline-block', width: '16px', fontSize: '12px', color: '#667eea' }}>
                                      {details.length > 0 ? (isExpanded ? '▼' : '▶') : ''}
                                    </span>
                                    {row.type === 'czyszczenie' ? t('downtime.cleaning') : t('downtime.breakdown')}
                                  </td>
                                  <td style={tdStyle}>{row.count}</td>
                                  <td style={tdStyle}>{row.total_minutes.toLocaleString('pl-PL', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}</td>
                                </tr>
                                {isExpanded && details.length > 0 && (
                                  <tr>
                                    <td colSpan={3} style={{ padding: '0', border: '1px solid #dee2e6' }}>
                                      <div style={{ backgroundColor: '#f8f9fc', padding: '12px 16px 12px 44px' }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                          <thead>
                                            <tr style={{ backgroundColor: '#e2e8f0' }}>
                                              <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: '600', color: '#4a5568', borderBottom: '1px solid #cbd5e0' }}>Start</th>
                                              <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: '600', color: '#4a5568', borderBottom: '1px solid #cbd5e0' }}>Koniec</th>
                                              <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: '600', color: '#4a5568', borderBottom: '1px solid #cbd5e0' }}>Czas trwania</th>
                                              <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: '600', color: '#4a5568', borderBottom: '1px solid #cbd5e0' }}>Operator</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {details.map((detail, di) => (
                                              <tr key={detail.id} style={{ backgroundColor: di % 2 === 0 ? '#f8f9fc' : '#eef2ff', borderBottom: '1px solid #e2e8f0' }}>
                                                <td style={{ padding: '7px 10px', textAlign: 'left' }}>{formatDate(detail.started_at)}</td>
                                                <td style={{ padding: '7px 10px', textAlign: 'left' }}>{formatDate(detail.ended_at)}</td>
                                                <td style={{ padding: '7px 10px', textAlign: 'right', fontWeight: '600', fontFamily: 'monospace' }}>{formatDuration(detail.duration_seconds)}</td>
                                                <td style={{ padding: '7px 10px', textAlign: 'left' }}>{detail.user_name || '-'}</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}
        </>
      )}
    </div>
  );
};

export default AnalyticsDashboard;
